import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import {
  Card,
  Stack,
  Container,
  Typography,
  Tooltip,
  Chip,
  Button,
} from '@mui/material';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { toast } from 'react-toastify';

import Iconify from '../../components/iconify';
import { API_URL } from '../../api';
import HowItWorksCard from '../../components/HowItWorksCard';
import { DataGrid, LoadPanel } from 'devextreme-react';
import {
  Column,
  FilterBuilderPopup,
  FilterPanel,
  FilterRow,
  GroupPanel,
  HeaderFilter,
  Pager,
  Paging,
  Scrolling,
  SearchPanel,
  Button as DevBtn,
} from 'devextreme-react/data-grid';

const cookies = new Cookies();

export default function OrderPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/admin`, {
        headers: {
          Authorization: cookies.get('TOKEN'),
        },
      });
      setOrders(response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return `£${(price / 100).toFixed(2)}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'default';
      default:
        return 'default';
    }
  };

  const statusCellRender = (data) => {
    return (
      <Chip
        label={data.value}
        color={getStatusColor(data.value)}
        size="small"
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };

  const totalCellRender = (data) => {
    return formatPrice(data.value);
  };

  const dateCellRender = (data) => {
    return formatDate(data.value);
  };

  const customerCellRender = (data) => {
    const customer = data.data?.customer;
    if (!customer) return '-';
    return (
      <div>
        <div style={{ fontWeight: 500 }}>{`${customer.firstName} ${customer.lastName}`}</div>
        <div style={{ fontSize: '0.85em', color: '#666' }}>{customer.email}</div>
      </div>
    );
  };

  const itemsCellRender = (data) => {
    const items = data.data?.items || [];
    return (
      <div>
        {items.map((item, index) => (
          <div key={index} style={{ fontSize: '0.9em' }}>
            {item.title}
          </div>
        ))}
      </div>
    );
  };

  const paymentMethodCellRender = (data) => {
    const icon = data.value === 'stripe' ? 'mdi:credit-card' : 'mdi:paypal';
    return (
      <Chip
        icon={<Iconify icon={icon} />}
        label={data.value}
        size="small"
        variant="outlined"
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };

  return (
    <>
      <Helmet>
        <title>Orders | English Teacher Admin</title>
      </Helmet>

      <Container>
        <Stack className="stacky">
          <Typography variant="h4" gutterBottom style={{ fontFamily: 'Inter', fontWeight: '600' }}>
            Shop Orders
          </Typography>
        </Stack>
        <Box sx={{ mb: 3 }}>
          <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Every Stripe order placed through the Shop lands in this list.<br />
                            <strong>2.</strong> Filter by product, buyer email, or date range. Click a row to see the full order detail and payment reference.<br />
                            <strong>3.</strong> Refunds are handled through the Stripe dashboard — this page is view-only for record-keeping and CSV export.<br />
                        </Typography>
                    </HowItWorksCard>
        </Box>


        <Card sx={{ borderRadius: '12px' }}>
          <DataGrid
            dataSource={orders}
            showBorders={true}
            autoExpandAll={true}
            columnAutoWidth={true}
            keyExpr="_id"
            wordWrapEnabled={true}
          >
            <LoadPanel enabled={loading} />
            <SearchPanel visible={true} highlightCaseSensitive={true} placeholder="Search orders..." />
            <FilterRow visible={true} />
            <HeaderFilter visible={true} />
            <GroupPanel visible={true} />
            <FilterPanel visible={true} />
            <FilterBuilderPopup position={{ of: window, at: 'top', my: 'top', offset: { y: 10 } }} />
            <Scrolling mode="standard" />
            <Paging defaultPageSize={20} />
            <Pager
              visible={true}
              allowedPageSizes={[10, 20, 50, 100]}
              showPageSizeSelector={true}
              displayMode="full"
              showInfo={true}
              showNavigationButtons={true}
            />

            <Column dataField="orderNumber" caption="Order #" width={140} />
            <Column
              caption="Customer"
              cellRender={customerCellRender}
              calculateCellValue={(data) => `${data.customer?.firstName} ${data.customer?.lastName} ${data.customer?.email}`}
              width={200}
            />
            <Column
              dataField="customer.buyerType"
              caption="Buyer Type"
              width={110}
            />
            <Column caption="Items" cellRender={itemsCellRender} width={200} />
            <Column dataField="total" caption="Total" cellRender={totalCellRender} width={100} />
            <Column dataField="paymentMethod" caption="Payment" cellRender={paymentMethodCellRender} width={100} />
            <Column dataField="paymentStatus" caption="Status" cellRender={statusCellRender} width={100} />
            <Column dataField="createdAt" caption="Date" cellRender={dateCellRender} dataType="datetime" width={150} />

            <Column type="buttons" caption="Actions" width={80}>
              <DevBtn onClick={(e) => navigate(`/orders/${e.row?.data?._id}`)}>
                <Button className="btn">
                  <Tooltip title="View Details" placement="top-start" arrow>
                    <Iconify icon="mdi:eye" />
                  </Tooltip>
                </Button>
              </DevBtn>
            </Column>
          </DataGrid>
        </Card>
      </Container>
    </>
  );
}